import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type MarketingTriage } from "@/lib/api";
import { cn } from "@/lib/formatters";

const ICONO: Record<string, string> = {
  linkedin: "💼", instagram: "📸", x: "𝕏", threads: "🧵",
  facebook: "📘", reddit: "🟠", tiktok: "🎵",
};

/** Cada red cuesta una corrida completa del agente. Es la referencia con
 *  precio de Opus 5 que ya usa el resumen, no una factura. */
const USD_POR_RED = 0.4;

/**
 * La decisión del triage, esperando el visto bueno.
 *
 * El triage dice para qué redes vale la pena; quien aprueba elige cuáles de
 * ésas. Antes era todo o nada, y eso obligaba a pagar cuatro redes para
 * publicar en una.
 *
 * Aprobar es lo que arranca la escritura —el contenido todavía no existe— así
 * que descartar no cuesta nada y el botón dice "Generar", no "Publicar".
 */
export function PendingApproval({ triage }: { triage: MarketingTriage }) {
  const qc = useQueryClient();
  const [descartado, setDescartado] = useState(false);
  const [abierta, setAbierta] = useState<string | null>(null);

  const van = triage.redes.filter((r) => r.generar);
  const noVan = triage.redes.filter((r) => !r.generar);

  // Arranca con todas tildadas: el triage ya filtró, y desmarcar es más rápido
  // que marcar de a una cuando lo normal es aceptar su criterio.
  const [elegidas, setElegidas] = useState<Set<string>>(
    () => new Set(van.map((r) => r.red)),
  );

  const aprobar = useMutation({
    mutationFn: () => api.marketing.approve(triage.slug, [...elegidas]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketing-pending"] }),
  });

  if (descartado) return null;

  const bloqueado = aprobar.isPending || aprobar.isSuccess;

  const alternar = (red: string) =>
    setElegidas((prev) => {
      const next = new Set(prev);
      if (next.has(red)) next.delete(red);
      else next.add(red);
      return next;
    });

  return (
    <div className="surface p-5 space-y-4 border-l-2 border-primary/50">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="path-chip">esperando aprobación</p>
          <h3 className="text-lg font-semibold mt-1 truncate">{triage.title}</h3>
          <p className="font-mono text-xs text-muted mt-0.5">{triage.slug}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="font-mono text-xs text-muted tabular-nums"
            title="Referencia con precio de Opus 5, no una factura"
          >
            ~${(elegidas.size * USD_POR_RED).toFixed(2)}
          </span>
          <button
            type="button"
            onClick={() => aprobar.mutate()}
            disabled={bloqueado || elegidas.size === 0}
            className={cn(
              "px-3 py-1.5 rounded-sm font-mono text-xs uppercase tracking-wider border transition-colors",
              "border-primary/40 text-primary hover:bg-primary/10",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            {aprobar.isSuccess
              ? "Generando…"
              : aprobar.isPending
                ? "Disparando…"
                : elegidas.size === 0
                  ? "Elegí una red"
                  : `Generar ${elegidas.size}`}
          </button>
          <button
            type="button"
            onClick={() => setDescartado(true)}
            disabled={bloqueado}
            className={cn(
              "px-3 py-1.5 rounded-sm font-mono text-xs uppercase tracking-wider border transition-colors",
              "border-border text-muted hover:text-text hover:border-danger/40",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
          >
            Descartar
          </button>
        </div>
      </div>

      {aprobar.isError && (
        <p className="text-sm text-danger">
          No se pudo disparar: {String(aprobar.error)}
        </p>
      )}

      {aprobar.isSuccess && (
        <p className="text-sm text-muted">
          El agente está escribiendo{" "}
          {elegidas.size === 1 ? "una red" : `${elegidas.size} redes`}. Tarda unos
          minutos y aparece abajo cuando termina.
        </p>
      )}

      {van.length === 0 ? (
        <p className="text-sm text-muted">
          Ninguna red superó el piso de fit. No hay nada que generar.
        </p>
      ) : (
        <ul className="space-y-1">
          {van.map((r) => {
            const tildada = elegidas.has(r.red);
            const expandida = abierta === r.red;
            return (
              <li
                key={r.red}
                className={cn(
                  "rounded-sm transition-colors",
                  tildada ? "bg-primary/[0.04]" : "opacity-50",
                )}
              >
                <div className="flex items-center gap-3 px-2 py-2">
                  <input
                    type="checkbox"
                    id={`red-${triage.slug}-${r.red}`}
                    checked={tildada}
                    disabled={bloqueado}
                    onChange={() => alternar(r.red)}
                    className="accent-primary shrink-0 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <label
                    htmlFor={`red-${triage.slug}-${r.red}`}
                    className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer"
                  >
                    <span aria-hidden>{ICONO[r.red] ?? "•"}</span>
                    <span className="text-sm font-medium">{r.red}</span>
                    <span className="font-mono text-xs text-muted">
                      {r.formato} · fit {r.fit}
                    </span>
                  </label>
                  {/* El porqué está siempre disponible, pero plegado: son
                      varias frases largas y cuatro juntas tapan la decisión. */}
                  <button
                    type="button"
                    onClick={() => setAbierta(expandida ? null : r.red)}
                    aria-expanded={expandida}
                    className="font-mono text-xs text-muted hover:text-primary transition-colors shrink-0"
                  >
                    {expandida ? "− por qué" : "+ por qué"}
                  </button>
                </div>
                {expandida && r.why && (
                  <p className="text-sm text-muted leading-relaxed px-2 pb-3 pl-9">
                    {r.why}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {noVan.length > 0 && (
        <details className="group">
          <summary className="font-mono text-xs text-muted cursor-pointer hover:text-text transition-colors list-none">
            <span className="group-open:hidden">+ </span>
            <span className="hidden group-open:inline">− </span>
            {noVan.length} saltadas sin costo
          </summary>
          <ul className="mt-2 space-y-2 pl-4">
            {noVan.map((r) => (
              <li key={r.red}>
                <p className="font-mono text-xs">
                  <span aria-hidden className="mr-1">{ICONO[r.red] ?? "•"}</span>
                  {r.red} · fit {r.fit}
                </p>
                {r.why && (
                  <p className="text-sm text-muted leading-relaxed mt-0.5">{r.why}</p>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
